<template>
    <ListWrapper
        :loading="loading"
        :error="error"
        :is-empty="!pedidos.length"
        :meta="meta"
        :page="page"
        :per-page="perPage"
        @fetch="emit('fetch', $event)"
    >
        <template #loading-skeleton>
            <Skeleton customClass="tw:mb-2" padding="tw:py-5 tw:px-5">
                <template #skeleton>
                    <SkeletonItem customClass="tw:w-full tw:h-8 tw:rounded-lg" />
                </template>
            </Skeleton>
            <Skeleton
                v-for="n in 3"
                :key="n"
                customClass="tw:mb-3 tw:overflow-hidden"
                padding="tw:p-0"
            >
                <template #skeleton>
                    <div class="tw:relative tw:h-44 tw:w-full">
                        <div class="tw:absolute tw:inset-0 tw:flex tw:flex-col tw:justify-end tw:p-4">
                            <div class="tw:flex tw:justify-between tw:items-end">
                                <div class="tw:space-y-2">
                                    <SkeletonItem customClass="tw:w-48" height="tw:h-5" />
                                    <SkeletonItem customClass="tw:w-32" height="tw:h-3" />
                                </div>
                                <SkeletonItem customClass="tw:w-20 tw:rounded-lg" height="tw:h-12" />
                            </div>
                        </div>
                    </div>
                </template>
            </Skeleton>
            <Skeleton padding="tw:py-2 tw:px-4">
                <template #skeleton>
                    <div class="tw:flex tw:justify-center tw:items-center tw:gap-1.5">
                        <SkeletonItem customClass="tw:w-6 tw:h-7" />
                        <SkeletonItem customClass="tw:w-6 tw:h-7" />
                        <SkeletonItem customClass="tw:w-6 tw:h-7" />
                    </div>
                </template>
            </Skeleton>
        </template>
        <template #filters>
            <CardFront custom-classes="tw:mb-3 tw:py-4 tw:px-4">
                <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-3 tw:justify-center">
                    <InputText
                        :value="search"
                        name="search"
                        :label="$t('perfil.buscar_pedido')"
                        custom-class="tw:flex-1"
                        @input="emit('update:search', $event)"
                    />
                    <InputMulti
                        :value="tipo"
                        name="tipo"
                        :options="tipoOptions"
                        value-key="value"
                        label-key="text"
                        custom-class="tw:sm:w-40"
                        @input="emit('update:tipo', $event)"
                    />
                    <Button variant="alt" custom-class="tw:sm:w-50 tw:whitespace-nowrap" @click="emit('toggle-invitaciones')">
                        <i class="material-icons tw:text-sm">mail</i>
                        {{ $t('perfil.ver_invitaciones') }}
                    </Button>
                </div>
            </CardFront>
        </template>

        <template #error="{ error: err }">
            <CardFront custom-classes="tw:py-16 tw:text-center tw:items-center">
                <i class="material-icons tw:text-5xl tw:text-red-300">error_outline</i>
                <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">{{ $t('perfil.pedidos_error_titulo') }}</p>
                <p class="tw:text-sm tw:text-slate-400 tw:mt-1">{{ err }}</p>
            </CardFront>
        </template>

        <template #empty>
            <CardFront custom-classes="tw:py-16 tw:text-center tw:items-center">
                <i class="material-icons tw:text-5xl tw:text-slate-300">confirmation_number</i>
                <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">{{ $t('perfil.no_hay_pedidos') }}</p>
                <p class="tw:text-sm tw:text-slate-400 tw:mt-1">{{ $t('perfil.no_hay_pedidos_descripcion') }}</p>
            </CardFront>
        </template>

        <PedidoCard
            v-for="pedido in pedidos"
            :key="pedido.id"
            :pedido="pedido"
            @cambiar-datos="emit('cambiar-datos', $event)"
            @enviar-entradas="emit('enviar-entradas', $event)"
            @edit-success="emit('edit-success')"
        />
    </ListWrapper>
</template>

<script setup>
    import { computed } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import PedidoCard from './PedidoCard.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import InputMulti from '@/modules/front/ui/InputMulti.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import ListWrapper from '@/modules/front/ui/ListWrapper.vue';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';

    const props = defineProps({
        pedidos:  { type: Array,   default: () => [] },
        loading:  { type: Boolean, default: false },
        error:    { type: String,  default: null },
        meta:     { type: Object,  default: null },
        page:     { type: Number,  default: 1 },
        perPage:  { type: Number,  default: 10 },
        search:   { type: String,  default: '' },
        tipo:     { type: String,  default: 'actuales' },
    });

    const emit = defineEmits(['fetch', 'update:search', 'update:tipo', 'cambiar-datos', 'enviar-entradas', 'toggle-invitaciones', 'edit-success']);

    const tipoOptions = computed(() => [
        { value: 'actuales', text: t('perfil.actuales') },
        { value: 'pasados',  text: t('perfil.pasados') },
    ]);
</script>
